/* eslint-disable no-nested-ternary, jsx-a11y/media-has-caption */
import PropTypes from 'prop-types';
import MicOutlined from '@mui/icons-material/MicOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import VolumeUpOutlined from '@mui/icons-material/VolumeUpOutlined';
import DeviceField from './DeviceField';

const statusText = (kind, status, t) => {
  if (status === 'ready') return t(`preCall.${kind}.ready`);
  if (status === 'idle' || status === 'requesting')
    return t(`preCall.${kind}.requesting`);
  if (status === 'missing') return t(`preCall.${kind}.missing`);
  if (status === 'denied') return t(`preCall.${kind}.blocked`);
  return t(`preCall.${kind}.failed`);
};

const DeviceSettingsFields = ({
  controller,
  t,
  cameraLocked = false,
  inSession = false,
  order = ['camera', 'microphone', 'speaker'],
}) => {
  const microphoneError = inSession && controller.errors?.microphone;
  const cameraError = inSession && controller.errors?.camera;
  const speakerError = inSession && controller.errors?.speaker;
  const microphoneHelp = microphoneError
    ? t('deviceSettings.microphone.error')
    : controller.changing?.microphone
    ? t('deviceSettings.microphone.changing')
    : statusText('microphone', controller.microphoneStatus || 'ready', t);
  const cameraHelp = cameraLocked
    ? t('deviceSettings.camera.rejoin')
    : cameraError
    ? t('deviceSettings.camera.error')
    : controller.changing?.camera
    ? t('deviceSettings.camera.changing')
    : statusText('camera', controller.cameraStatus || 'ready', t);
  const speakerHelp = !controller.speakerSupported
    ? t('preCall.speaker.unsupported')
    : speakerError
    ? t('deviceSettings.speaker.error')
    : controller.changing?.speaker
    ? t('deviceSettings.speaker.changing')
    : controller.playing
    ? t('preCall.speaker.playing')
    : t('preCall.speaker.help');

  const fields = {
    camera: (
      <DeviceField
        key="camera"
        icon={<VideocamOutlined />}
        label={t('preCall.camera.label')}
        options={controller.cameraStatus === 'denied' ? [] : controller.cameras}
        value={
          controller.cameraStatus === 'denied'
            ? ''
            : controller.selectedCameraId
        }
        disabled={
          cameraLocked ||
          controller.cameraStatus === 'denied' ||
          controller.changing?.camera
        }
        onChange={controller.selectCamera}
        helpText={cameraHelp}
      />
    ),
    microphone: (
      <DeviceField
        key="microphone"
        icon={<MicOutlined />}
        label={t('preCall.microphone.label')}
        options={
          controller.microphoneStatus === 'denied' ? [] : controller.microphones
        }
        value={
          controller.microphoneStatus === 'denied'
            ? ''
            : controller.selectedMicrophoneId
        }
        disabled={
          controller.microphoneStatus === 'denied' ||
          controller.changing?.microphone
        }
        onChange={controller.selectMicrophone}
        helpText={microphoneHelp}
        progress={
          controller.microphoneStatus === 'ready' || inSession
            ? controller.micLevel
            : ['denied', 'missing', 'failed'].includes(
                controller.microphoneStatus,
              )
            ? 'critical'
            : undefined
        }
      />
    ),
    speaker: (
      <DeviceField
        key="speaker"
        icon={<VolumeUpOutlined />}
        label={t('preCall.speaker.label')}
        options={
          controller.speakerSupported
            ? controller.speakers
            : controller.speakers.filter(option => option.value === 'default')
        }
        value={controller.selectedSpeakerId}
        disabled={
          inSession &&
          (!controller.speakerSupported || controller.changing?.speaker)
        }
        onChange={controller.selectSpeaker}
        helpText={speakerHelp}
        action={
          <button
            type="button"
            className="vonage-device-field__action"
            disabled={controller.playing}
            onClick={controller.playSpeakerTest}
          >
            {t('preCall.speaker.test')}
          </button>
        }
      />
    ),
  };

  return (
    <div className="vonage-device-fields">
      {order.map(kind => fields[kind])}
      <audio aria-hidden="true" ref={controller.audioElementRef} />
    </div>
  );
};

DeviceSettingsFields.propTypes = {
  controller: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  cameraLocked: PropTypes.bool,
  inSession: PropTypes.bool,
  order: PropTypes.arrayOf(
    PropTypes.oneOf(['camera', 'microphone', 'speaker']),
  ),
};

export default DeviceSettingsFields;
